import { useState } from "react";
import { validateRenewal } from "../SubscriptionsUtils/validation";

export default function RenewSubscriptionModal({
  currentCost,
  currentCurrency = "₹",
  dueDate,
  onConfirm,
  onClose
}) {
  const [cost, setCost] = useState(currentCost);
  const [costCurrency, setCostCurrency] = useState(currentCurrency);
  const [errors, setErrors] = useState({});

  const hasDueDate = !!dueDate;
  const initialMode = hasDueDate ? "from_due_date" : "from_today";
  const [renewMode, setRenewMode] = useState(initialMode);
  const [customStartDate, setCustomStartDate] = useState("");

  const formatShortDate = (value) => {
    if (!value) return "Not set";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch {
      return String(value);
    }
  };

  const handleConfirm = () => {
    const validation = validateRenewal({ newCost: cost });
    const nextErrors = { ...validation.errors };

    if (renewMode === "from_custom" && !customStartDate) {
      nextErrors.customStartDate = "Please select a start date";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});

    onConfirm({
      newCost: Number(cost),
      costCurrency,
      renewMode,
      customStartDate: renewMode === "from_custom" ? customStartDate : undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-[480px]">
        <h2 className="text-lg font-semibold mb-4">
          Renew Subscription
        </h2>

        <div className="space-y-4">
          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Renewal Cost <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={costCurrency}
                onChange={(e) => setCostCurrency(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
                aria-label="Currency"
              >
                <option value="₹">₹</option>
                <option value="$">$</option>
                <option value="€">€</option>
              </select>

              <input
                type="number"
                min="0"
                step="0.01"
                value={cost}
                onChange={(e) => {
                  setCost(e.target.value);
                  if (errors.newCost) setErrors({ ...errors, newCost: "" });
                }}
                placeholder="Enter renewal cost"
                className={`w-full border rounded px-3 py-2 text-sm ${errors.newCost ? "border-red-500" : ""}`}
              />
            </div>
            {errors.newCost && (
              <p className="text-xs text-red-500 mt-1">{errors.newCost}</p>
            )}
            <p className="text-xs text-gray-500 mt-1.5">
              This is the cost for the next billing cycle.
            </p>
          </div>

          {/* Mode selection */}
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1.5">
              Renewal period
            </p>
            <div className="space-y-2">
              {hasDueDate && (
                <label className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="renew-mode"
                    value="from_due_date"
                    checked={renewMode === "from_due_date"}
                    onChange={() => setRenewMode("from_due_date")}
                    className="mt-1"
                  />
                  <div className="text-xs sm:text-sm">
                    <p className="font-medium text-gray-800">
                      From last expiry date
                    </p>
                    <p className="text-gray-500">
                      Last expiry: {formatShortDate(dueDate)} · Cost: {costCurrency}{" "}
                      {cost || currentCost}
                    </p>
                  </div>
                </label>
              )}

              <label className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="renew-mode"
                  value="from_today"
                  checked={renewMode === "from_today"}
                  onChange={() => setRenewMode("from_today")}
                  className="mt-1"
                />
                <div className="text-xs sm:text-sm">
                  <p className="font-medium text-gray-800">
                    From today
                  </p>
                  <p className="text-gray-500">
                    Start: {formatShortDate(new Date())} · Cost: {costCurrency}{" "}
                    {cost || currentCost}
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="renew-mode"
                  value="from_custom"
                  checked={renewMode === "from_custom"}
                  onChange={() => setRenewMode("from_custom")}
                  className="mt-1"
                />
                <div className="flex-1 text-xs sm:text-sm">
                  <p className="font-medium text-gray-800">
                    Select custom start date
                  </p>
                  <div className="mt-1 flex flex-col sm:flex-row gap-2 sm:items-center">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => {
                        setCustomStartDate(e.target.value);
                        if (errors.customStartDate) {
                          setErrors({ ...errors, customStartDate: "" });
                        }
                      }}
                      className={`border rounded px-3 py-1.5 text-xs sm:text-sm ${
                        errors.customStartDate ? "border-red-500" : ""
                      }`}
                    />
                    <span className="text-gray-500">
                      Cost: {costCurrency} {cost || currentCost}
                    </span>
                  </div>
                  {errors.customStartDate && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.customStartDate}
                    </p>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
          <button onClick={onClose} className="group relative px-5 py-2.5 rounded-xl border-2 border-gray-300 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-white hover:border-gray-400 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 font-medium text-sm text-gray-700 hover:text-gray-900 shadow-sm hover:shadow-md w-full sm:w-auto">
            <span className="relative z-10">Cancel</span>
          </button>
          <button
            onClick={handleConfirm}
            className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 overflow-hidden w-full sm:w-auto"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
            <span className="relative z-10">Renew</span>
          </button>
        </div>
      </div>
    </div>
  );
}

